import type { ObservableMap } from 'mobx'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { getSlides, touchSlide } from '../firestore/Slide'
import {
  addUserProfileFavoriteSlideDeck,
  getUserProfileFavoriteSlideDecks,
  removeUserProfileFavoriteSlideDeck,
} from '../firestore/UserProfileFavoriteSlideDeck'
import { UserProfileFavoriteSlideDeck as UserProfileFavoriteSlideDeckModel } from '../models/UserProfileFavoriteSlideDeck'
import {
  deepCopySlideDeck,
  getSlideDeck,
  saveSlideDeckForm,
  addCatalogToSlideDeck,
  updateSlideDeckFeatured,
  removeCatalogFromSlideDeck,
  uploadSlideDeckImage,
  deleteSlideDeckImage,
  deleteSlideDeck,
  removeCatalogFromSlideDeckAndUnFeature,
  hideSlideDeck,
  showSlideDeck,
  touchSlideDeck,
  forkSlideDeck,
  updateSlideDeckState,
} from '../firestore/SlideDeck'
import { getSlideDecksWithTypeId } from '../firestore/SlideDeck'
import type { ExhibitFieldsForUpload } from '../firestore/SlideDeckExhibit'
import {
  deleteSlideDeckExhibit,
  deleteSlideDeckExhibitImage,
  getSlideDeckExhibits,
  saveSlideDeckExhibit,
  uploadSlideDeckExhibitImage,
} from '../firestore/SlideDeckExhibit'
import {
  getSlideDeckRoles,
  saveSlideDeckRole,
  deleteSlideDeckRole,
  sortSlideDeckRoles,
} from '../firestore/SlideDeckRole'
import { SlideDeckRole } from '../models/SlideDeckRole'
import {
  deleteSlideDeckMaterial,
  deleteSlideDeckMaterialFile,
  getSlideDeckMaterials,
  saveSlideDeckMaterial,
  sortSlideDeckMaterials,
  uploadSlideDeckMaterialFile,
} from '../firestore/SlideDeckMaterial'
import {
  deleteSlide,
  deleteSlideImage,
  deleteSlideVideo,
  saveSlide,
  sortSlides,
  uploadSlideImage,
  uploadSlideVideo,
} from '../firestore/Slide'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { SlideDeck, SlideDeckState, SlideDeckStatus } from '../models/SlideDeck'
import { SlideDeckExhibit } from '../models/SlideDeckExhibit'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { SlideModel } from '../models/SlideModel'
import type { RoleFields } from '../types'
import { SlideDeckType, type StaticModelCollection } from '../types'
import { SlideDeckMaterialType } from '../types'
import { Cubit } from './core'
import { SlideDeckAuthor } from '../models/SlideDeckAuthor'
import type { AuthorFieldsForUpload } from '../firestore/SlideDeckAuthor'
import {
  deleteSlideDeckAuthor,
  deleteSlideDeckAuthorImage,
  getSlideDeckAuthors,
  saveSlideDeckAuthor,
  sortSlideDeckAuthors,
  uploadSlideDeckAuthorImage,
} from '../firestore/SlideDeckAuthor'
import {
  getSoloBotAudio,
  type QuestionFieldsForUpload,
  sortSlideQuestions,
} from '../firestore/SlideQuestion'
import {
  deleteSlideDeckQuestion,
  getSlideQuestions,
  getTalkTrackAudio,
  saveSlideDeckQuestion,
} from '../firestore/SlideQuestion'
import { SlideQuestion } from '../models/SlideQuestion'
import { SlideRubric } from '../models/SlideRubric'
import {
  assignSlideRubricToSlide,
  deleteSlideRubric,
  getSlideRubrics,
  saveSlideRubric,
} from '../firestore/SlideRubric'
import { getSettingsSlideDeckFields } from '../firestore/SettingsSlideDeckFields'
import { SettingsSlideDeckFields } from '../models/SettingsSlideDeckFields'
import { Catalog } from '../models/Catalog'
import { getCatalogs } from '../firestore/Catalog'
import { SlideDeckReference } from '../models/SlideDeckReference'
import {
  deleteSlideDeckReference,
  getSlideDeckReferences,
  saveSlideDeckReference,
  sortSlideDeckReferences,
} from '../firestore/SlideDeckReference'
import {
  deleteTeachingPlanModuleSlideDeck,
  getSlideDeckModulesForSlideDeckType,
  saveTeachingPlanModuleSlideDeck,
} from '../firestore/TeachingPlanModuleSlideDeck'
import { TeachingPlanModuleSlideDeck } from '../models/TeachingPlanModuleSlideDeck'
import { TeachingPlanModule } from '../models/TeachingPlanModule'
import { TeachingPlan } from '../models/TeachingPlan'
import { getTeachingPlan } from '../firestore/TeachingPlan'
import { getTeachingPlanModule } from '../firestore/TeachingPlanModule'
import { runTransaction } from 'firebase/firestore'
import { SettingsSlideDeckTag } from '../models/SettingsSlideDeckTag'
import { getSettingsSlideDeckTags } from '../firestore/SettingsSlideDeckTag'
import { validateSlideDeck } from '../util/slideDeckValidation'
import type { Media } from '../models/Media'
import { getMedia } from '../firestore/Media'
import { type FirestoreSlideDeck } from '../firestore/SlideDeck/schema'
import { sortRubrics } from '../sources-of-truth/sortRubrics'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { ensureDevRoomState } from '../firestore/RoomState'
import { PromptInteractiveElementType } from '../models/PromptInteractiveElementType'
import { getPromptInteractiveElementTypes } from '../firestore/PromptInteractiveElementType'
import {
  createSlideDeckReviewInvitation,
  getSlideDeckHasReviewInvites,
} from '../firestore/Invitation'
import { fetchUserSearch, getUserSearch } from '../firestore/UserSearch'
import type { UserSearch } from '../stores/UserSearch'

export enum AdminSlideDeckTab {
  experienceDetails = 'experience_details',
  slides = 'slides',
  interactiveElements = 'interactive_elements',
  rubrics = 'rubrics',
  exhibits = 'exhibits',
  courseMaterials = 'course_materials',
  authors = 'authors',
  references = 'references',
  roles = 'roles',
}

enum SlideQuestionBucketType {
  poll = 'poll',
  quiz = 'quiz',
  title = 'title',
  talkTrack = 'talkTrack',
  soloBot = 'soloBot',
  displayModerationTask = 'displayModerationTask',
  moderationTask = 'moderationTask',
  sortableStringList = 'sortableStringList',
  popUpInstructions = 'popUpInstructions',
  interactiveFormElement = 'interactiveFormElement',
  aiAgent = 'aiAgent',
  suggestedTimer = 'suggestedTimer',
}

class SlideQuestionBucket {
  slideId: string
  slideQuestionBucketType: SlideQuestionBucketType
  usingGroupSlideId: boolean
  questions: SlideQuestion[]

  constructor({
    slideId,
    slideQuestionBucketType,
    usingGroupSlideId,
    questions,
  }: {
    slideId: string
    slideQuestionBucketType: SlideQuestionBucketType
    usingGroupSlideId: boolean
    questions: SlideQuestion[]
  }) {
    this.slideId = slideId
    this.slideQuestionBucketType = slideQuestionBucketType
    this.usingGroupSlideId = usingGroupSlideId
    this.questions = questions
  }

  bucketMatches({
    slideId,
    slideQuestionBucketType,
    usingGroupSlideId,
  }: {
    slideId: string
    slideQuestionBucketType: SlideQuestionBucketType
    usingGroupSlideId: boolean
  }) {
    return (
      this.slideId === slideId &&
      this.slideQuestionBucketType === slideQuestionBucketType &&
      this.usingGroupSlideId === usingGroupSlideId
    )
  }
}

export class AdminSlideDeckCubit extends Cubit {
  repository: FirebaseRepository

  slideDeck: SlideDeck
  slideDeckId: string

  materials: StaticModelCollection<SlideDeckMaterial>
  interactiveElementPrompts: StaticModelCollection<PromptInteractiveElementType>
  exhibits: StaticModelCollection<SlideDeckExhibit>
  settingsSlideDeckFields: SettingsSlideDeckFields
  settingsSlideDeckTags: StaticModelCollection<SettingsSlideDeckTag>
  authors: StaticModelCollection<SlideDeckAuthor>
  private _catalogs: StaticModelCollection<Catalog>
  private _slides: StaticModelCollection<SlideModel>
  private _questions: StaticModelCollection<SlideQuestion>
  private _references: StaticModelCollection<SlideDeckReference>
  private _rubrics: StaticModelCollection<SlideRubric>
  roles: StaticModelCollection<SlideDeckRole>
  private _moduleSlideDecks: StaticModelCollection<TeachingPlanModuleSlideDeck>

  @observable //key = `teachingPlan-$catalogId-$teachingPlanId`
  private _teachingPlanDataByIds: Record<string, TeachingPlan> = {}

  @observable //key = `module-$catalogId-$teachingPlanId-$moduleId`
  private _teachingPlanModulesByIds: Record<string, TeachingPlanModule> = {}

  slideDecksOfType: StaticModelCollection<SlideDeck>
  favoriteSlideDecks: StaticModelCollection<UserProfileFavoriteSlideDeckModel>
  @observable favoritingSlideDeckIds = observable.set<string>()
  @observable slideDeckHasReviewInvitations = false

  @observable primaryDesignerSearch: UserSearch | null = null
  @observable designedForInstructorSearch: UserSearch | null = null

  mediaObjects: ObservableMap<string, Media>

  constructor(repository: FirebaseRepository, slideDeckId: string) {
    super()
    makeObservable(this)

    this.slideDeckId = slideDeckId

    this.repository = repository
    this.slideDeck = SlideDeck.empty(repository)

    this.materials = SlideDeckMaterial.emptyCollection(repository)
    this.exhibits = SlideDeckExhibit.emptyCollection(repository)
    this.settingsSlideDeckFields = SettingsSlideDeckFields.empty(repository)
    this.authors = SlideDeckAuthor.emptyCollection(repository)
    this._catalogs = Catalog.emptyCollection(repository)
    this._slides = SlideModel.emptyCollection(repository)
    this._questions = SlideQuestion.emptyCollection(repository)
    this._references = SlideDeckReference.emptyCollection(repository)
    this._rubrics = SlideRubric.emptyCollection(repository)
    this.slideDecksOfType = SlideDeck.emptyCollection(repository)
    this.favoriteSlideDecks =
      UserProfileFavoriteSlideDeckModel.emptyCollection(repository)
    this._moduleSlideDecks =
      TeachingPlanModuleSlideDeck.emptyCollection(repository)
    this.settingsSlideDeckTags =
      SettingsSlideDeckTag.emptyCollection(repository)
    this.mediaObjects = observable.map<string, Media>({}, { deep: false })
    this.interactiveElementPrompts =
      PromptInteractiveElementType.emptyCollection(repository)
    this.roles = SlideDeckRole.emptyCollection(repository)
  }

  initialize(): void {
    this.addStream(
      getSlideDeck(this.repository, {
        slideDeckId: this.slideDeckId,
      }),
      (newSlideDeck) => {
        const lastType = this.slideDeck.data.slideDeckTypeId
        const updatedType = newSlideDeck.data.slideDeckTypeId

        // if the type has changed, we need to update the slideDecksOfType collection
        if (lastType !== updatedType) {
          // replace slide decks of type stream
          const keyFromTypeId = (typeId: string) => `slideDecksOfType-${typeId}`
          this.removeStream(keyFromTypeId(lastType))
          this.addStream(
            getSlideDecksWithTypeId(this.repository, {
              slideDeckTypeId: updatedType,
            }),
            (slideDecks) => {
              this.slideDecksOfType.replaceModels(slideDecks)
            },
            { name: keyFromTypeId(updatedType) }
          )

          // replace teachingPlanModulesForSlideDeckType stream
          const keyFromTypeIdForModules = (typeId: string) =>
            `teachingPlanModulesForSlideDeckType-${typeId}`
          this.removeStream(keyFromTypeIdForModules(lastType))
          this.addStream(
            getSlideDeckModulesForSlideDeckType(this.repository, {
              slideDeckTypeId: updatedType,
            }),
            (slideDeckModules) => {
              const teachingPlansToFetch: {
                catalogId: string
                teachingPlanId: string
              }[] = []
              const modulesToFetch: {
                catalogId: string
                teachingPlanId: string
                moduleId: string
              }[] = []
              slideDeckModules.forEach((module) => {
                teachingPlansToFetch.push({
                  catalogId: module.data.catalogId,
                  teachingPlanId: module.data.teachingPlanId,
                })
                modulesToFetch.push({
                  catalogId: module.data.catalogId,
                  teachingPlanId: module.data.teachingPlanId,
                  moduleId: module.data.moduleId,
                })
              })
              this.initTeachingPlanModuleStreams(modulesToFetch)
              this.initTeachingPlanStreams(teachingPlansToFetch)
              this._moduleSlideDecks.replaceModels(slideDeckModules)
            },
            { name: keyFromTypeIdForModules(updatedType) }
          )
        }
        const lastDesignerId = this.slideDeck.data.primaryDesignerUserId
        const newDesignerId = newSlideDeck.data.primaryDesignerUserId
        if (lastDesignerId !== newDesignerId) {
          this.restartPrimaryDesignerStream(newDesignerId)
        }

        const lastInstructorId = this.slideDeck.data.designedForInstructorUserId
        const newInstructorId = newSlideDeck.data.designedForInstructorUserId
        if (lastInstructorId !== newInstructorId) {
          this.restartDesignedForInstructorStream(newInstructorId)
        }

        this.slideDeck.replaceModel(newSlideDeck)
      }
    )

    // load materials on init since we need them for the details tab
    // not just the course materials tab
    this.addStream(
      getSlideDeckMaterials(this.repository, { slideDeckId: this.slideDeckId }),
      (materials) => {
        this.materials.replaceModels(materials)
      }
    )

    // load authors on init since we need them for the details tab
    // when we run validation.
    this.addStream(
      getSlideDeckAuthors(this.repository, { slideDeckId: this.slideDeckId }),
      (authors) => {
        this.authors.replaceModels(authors)
      }
    )

    this.addStream(
      getSlideDeckExhibits(this.repository, { slideDeckId: this.slideDeckId }),
      (exhibits) => {
        this.exhibits.replaceModels(exhibits)
      }
    )

    // load roles on init
    this.addStream(
      getSlideDeckRoles(this.repository, { slideDeckId: this.slideDeckId }),
      (roles) => {
        this.roles.replaceModels(roles)
      }
    )

    this.addStream(
      getSlides(this.repository, { slideDeckId: this.slideDeckId }),
      (slides) => {
        this.slides.replaceModels(slides)
        // loop over slides and if any have a global media object load it
        slides.forEach((slide) => {
          const mediaId = slide.data.mediaId
          if (mediaId) {
            if (!this.hasStream(`media-${mediaId}`)) {
              this.addStream(
                getMedia(this.repository, { mediaId }),
                (media) => {
                  this.mediaObjects.set(mediaId, media)
                },
                { namespace: `media`, name: `media-${mediaId}` }
              )
            }
          }
        })
      }
    )
    this.addStream(
      getSettingsSlideDeckFields(this.repository),
      (deckFields) => {
        this.settingsSlideDeckFields.replaceModel(deckFields)
      },
      {
        name: 'settings-slide-deck-fields',
      }
    )
    this.addStream(getSettingsSlideDeckTags(this.repository), (tags) => {
      this.settingsSlideDeckTags.replaceModels(tags)
    })

    if (this.repository.currentUser?.uid) {
      this.addStream(
        getUserProfileFavoriteSlideDecks(this.repository, {
          userId: this.repository.currentUser.uid,
        }),
        (favorites) => {
          this.favoriteSlideDecks.replaceModels(favorites)
        }
      )
    }

    this.addStream(
      getPromptInteractiveElementTypes(this.repository),
      (prompts) => {
        this.interactiveElementPrompts.replaceModels(prompts)
      }
    )

    this.addStream(
      getSlideDeckHasReviewInvites(this.repository, {
        slideDeckId: this.slideDeckId,
      }),
      (hasInvites) => {
        runInAction(() => {
          this.slideDeckHasReviewInvitations = hasInvites
        })
      }
    )
  }

  /**
   * Users with 'editor' role can modify some primitive fields of the slide deck after it is published
   */
  @computed
  get editablePrimitiveFields() {
    const { breakoutUser, featureFlags } = this.repository

    if (
      !breakoutUser?.isEditor ||
      !featureFlags.data.allowPublishedSlideDeckEdit
    )
      return new Set<keyof FirestoreSlideDeck>()

    return new Set<keyof FirestoreSlideDeck>([
      'slideDeckDescription',
      'slideDeckDisciplines',
      'slideDeckGoogleTemplateURL',
      'slideDeckImageURL',
      'slideDeckIndustries',
      'slideDeckKeyConcepts',
      'slideDeckLearningObjectives',
      'slideDeckName',
      'slideDeckTags',
      'slideDeckTeaser',
      'slideDeckPhraseSet',
      'primaryDesignerUserId',
      'designedForInstructorUserId',
    ])
  }

  @computed
  get teachingPlanModulesForSlideDeckByCatalogId() {
    const startData = this.slideDeckModulesForSlideDecksOfTypeByCatalogId
    const newData: Record<
      string,
      { teachingPlan: TeachingPlan; module: TeachingPlanModule }[]
    > = {}
    for (const key in startData) {
      const moduleDataForCatalog = startData[key]
      newData[key] = moduleDataForCatalog
        .filter((moduleData) => moduleData.slideDeck.id === this.slideDeckId)
        .map((moduleData) => ({
          teachingPlan: moduleData.teachingPlan,
          module: moduleData.teachingPlanModule,
        }))
    }
    return newData
  }

  @computed
  get moduleSlideDecksOfTypeDataLoading() {
    return (
      this._catalogs.isLoading ||
      this._moduleSlideDecks.isLoading ||
      this.slideDecksOfType.isLoading ||
      Object.values(this._teachingPlanDataByIds).some(
        (plan) => plan.isLoading
      ) ||
      Object.values(this._teachingPlanModulesByIds).some(
        (module) => module.isLoading
      )
    )
  }

  @computed
  get slideDeckModulesForSlideDecksOfTypeByCatalogId() {
    const moduleBundleByCatalogId: Record<
      string,
      {
        teachingPlanModuleSlideDeck: TeachingPlanModuleSlideDeck
        teachingPlanModule: TeachingPlanModule
        teachingPlan: TeachingPlan
        catalog: Catalog
        slideDeck: SlideDeck
      }[]
    > = {}
    this._moduleSlideDecks.models.forEach((moduleSlideDeck) => {
      const { catalogId, teachingPlanId, moduleId } = moduleSlideDeck.data
      const moduleKey = this.getTeachingPlanModuleKey({
        catalogId,
        teachingPlanId,
        moduleId,
      })
      const teachingPlanKey = this.getTeachingPlanKey({
        catalogId,
        teachingPlanId,
      })
      if (!moduleBundleByCatalogId[catalogId])
        moduleBundleByCatalogId[moduleSlideDeck.data.catalogId] = []
      moduleBundleByCatalogId[catalogId].push({
        teachingPlanModuleSlideDeck: moduleSlideDeck,
        teachingPlanModule:
          moduleKey in this._teachingPlanModulesByIds
            ? this._teachingPlanModulesByIds[moduleKey]
            : TeachingPlanModule.empty(this.repository),
        teachingPlan:
          teachingPlanKey in this._teachingPlanDataByIds
            ? this._teachingPlanDataByIds[teachingPlanKey]
            : TeachingPlan.empty(this.repository),
        catalog:
          this._catalogs.models.find((catalog) => catalog.id === catalogId) ||
          Catalog.empty(this.repository),
        slideDeck:
          this.slideDecksOfType.models.find(
            (slideDeck) => slideDeck.id === moduleSlideDeck.id
          ) || SlideDeck.empty(this.repository),
      })
    })
    return moduleBundleByCatalogId
  }

  /**
   * does not include the current slide deck
   */
  @computed
  get existingSlideDeckVersions() {
    const existingVersions: Set<string> = new Set()
    for (const slideDeck of this.slideDecksOfType.models) {
      // ignore self
      if (slideDeck.id === this.slideDeckId) continue
      existingVersions.add(slideDeck.data.slideDeckVersion)
    }
    return existingVersions
  }

  /**
   * does not include the current slide deck
   */
  @computed
  get slideDecksOfTypeByCatalogId() {
    const slideDecksByCatalog: Record<string, SlideDeck[]> = {}
    for (const slideDeck of this.slideDecksOfType.models) {
      // ignore self
      if (slideDeck.id === this.slideDeckId) continue
      for (const catalogId of slideDeck.data.catalogIds) {
        if (!slideDecksByCatalog[catalogId]) {
          slideDecksByCatalog[catalogId] = []
        }
        slideDecksByCatalog[catalogId].push(slideDeck)
      }
    }
    return slideDecksByCatalog
  }

  @computed
  get catalogs() {
    const name = 'catalogs'
    if (!this.hasStream(name)) {
      this.addStream(
        getCatalogs(this.repository),
        (catalogs) => {
          this._catalogs.replaceModels(
            catalogs.sort((a, b) =>
              a.data.catalogName.localeCompare(b.data.catalogName)
            )
          )
        },
        { name }
      )
    }
    return this._catalogs
  }

  @computed
  get questions(): StaticModelCollection<SlideQuestion> {
    if (this.hasStream('questions')) {
      return this._questions
    }

    this.addStream(
      getSlideQuestions(this.repository, { slideDeckId: this.slideDeckId }),
      (questions) => {
        this._questions.replaceModels(questions)
      },
      {
        name: 'questions',
      }
    )

    return this._questions
  }

  @computed
  get sortedQuestions(): SlideQuestion[] {
    const slideIds = this.slides.models.map((e) => e.id)
    return sortQuestions({
      questions: this.questions.models,
      slideIds,
      sortNoSlideFirst: true,
    })
  }

  @computed
  get publishedDemosOfType() {
    return this.slideDecksOfType.models.filter(
      (s) =>
        s.data.slideDeckType === SlideDeckType.demo &&
        s.slideDeckStatus === SlideDeckStatus.published
    )
  }

  get sortedQuestionsBuckets(): SlideQuestionBucket[] {
    const questions = this.sortedQuestions
    const buckets: SlideQuestionBucket[] = []

    const bucketsBySlideIdAndType: Record<
      string,
      Record<SlideQuestionBucketType, SlideQuestionBucket>
    > = {}
    questions.forEach((question) => {
      const slideId = question.data.slideId || question.data.groupSlideId || ''
      const usingGroupSlideId =
        !!question.data.groupSlideId && !question.data.slideId
      const questionType = question.questionType

      if (questionType === undefined) return

      const slideQuestionBucketType =
        slideQuestionBucketTypeFromQuestionType(questionType)

      let matchingBucket =
        bucketsBySlideIdAndType[slideId]?.[slideQuestionBucketType]

      if (!matchingBucket) {
        if (!bucketsBySlideIdAndType[slideId]) {
          bucketsBySlideIdAndType[slideId] = {} as Record<
            SlideQuestionBucketType,
            SlideQuestionBucket
          >
        }
        const newBucket = new SlideQuestionBucket({
          slideId: slideId,
          slideQuestionBucketType,
          usingGroupSlideId,
          questions: [],
        })
        bucketsBySlideIdAndType[slideId][slideQuestionBucketType] = newBucket

        matchingBucket = newBucket

        buckets.push(newBucket)
      }

      if (
        matchingBucket.bucketMatches({
          slideId,
          slideQuestionBucketType,
          usingGroupSlideId,
        })
      ) {
        matchingBucket.questions.push(question)
      } else {
        const currentBucket = new SlideQuestionBucket({
          slideId,
          slideQuestionBucketType,
          usingGroupSlideId,
          questions: [question],
        })
        buckets.push(currentBucket)
      }
    })

    for (const bucket of buckets) {
      const allQuestionsHaveOrder = bucket.questions.every(
        (question) => question.data.order !== undefined
      )
      if (allQuestionsHaveOrder) {
        bucket.questions.sort((a, b) => a.data.order! - b.data.order!)
      }
      bucket.questions = sortQuestions({
        questions: bucket.questions,
        slideIds: [],
      })
    }

    // we need empty bucket if no questions exist
    if (!buckets.length) {
      buckets.push(
        new SlideQuestionBucket({
          // these args don't really matter since questions is empty
          slideId: '',
          slideQuestionBucketType: SlideQuestionBucketType.poll,
          usingGroupSlideId: false,
          questions: [],
        })
      )
    }

    return buckets
  }

  @computed
  get slides(): StaticModelCollection<SlideModel> {
    const slidesKey = 'admin-slides'
    if (this.hasStream(slidesKey)) {
      return this._slides
    }

    this.addStream(
      getSlides(this.repository, { slideDeckId: this.slideDeckId }),
      (slides) => {
        this._slides.replaceModels(slides)
      },
      {
        name: slidesKey,
      }
    )

    return this._slides
  }

  @computed
  get references() {
    if (this.hasStream('references')) {
      return this._references
    }
    this.addStream(
      getSlideDeckReferences(this.repository, {
        slideDeckId: this.slideDeckId,
      }),
      (references) => {
        this._references.replaceModels(references)
      },
      {
        name: 'references',
      }
    )

    return this._references
  }

  @computed
  get rubrics() {
    const rubricsKey = 'rubrics'
    if (!this.hasStream(rubricsKey)) {
      this.addStream(
        getSlideRubrics(this.repository, {
          slideDeckId: this.slideDeckId,
        }),
        (rubrics) => {
          this._rubrics.replaceModels(rubrics)
        },
        {
          name: rubricsKey,
        }
      )
    }
    // sort rubrics by slideId
    return this._rubrics
  }

  @computed
  get sortedRubrics() {
    return sortRubrics(this.rubrics.models, this.slides.models)
  }

  @computed
  get meetsFeaturedRequirements() {
    const hasFeaturedImageMaterial = this.materials.models.some(
      (material) =>
        material.data.materialType === SlideDeckMaterialType.featuredLarge &&
        !!material.data.materialLink
    )

    const slideDeckInHealthyState =
      !!this.slideDeck.data.slideDeckName &&
      !!this.slideDeck.data.slideDeckImageURL &&
      this.slideDeck.slideDeckStatus !== SlideDeckStatus.hidden
    return (
      this.slideDeck.data.slideDeckFeatured ||
      (hasFeaturedImageMaterial && slideDeckInHealthyState)
    )
  }

  @computed
  get featuredImageMaterial() {
    return this.materials.models.find(
      (material) =>
        material.data.materialType === SlideDeckMaterialType.featuredLarge &&
        !!material.data.materialLink
    )
  }

  @action
  checkForReasonsNotToPublish = async () => {
    // Ensure all data required for validation is loaded
    await Promise.all([
      this.slideDeck.ready(),
      this.rubrics.ready(),
      this.slides.ready(),
      this.materials.ready(),
      this.authors.ready(),
      this.exhibits.ready(),
      this.questions.ready(),
    ])

    const context = {
      slideDeck: this.slideDeck.data,
      slides: this.slides.models,
      materials: this.materials.models.map((material) => material.data),
      authors: this.authors.models.map((author) => author.data),
      exhibits: this.exhibits.models,
      featuredImageMaterial: this.featuredImageMaterial?.data,
      repository: this.repository,
      slideDeckId: this.slideDeckId,
      firstSlideId: this.slides.models[0]?.id,
      mediaObjects: Object.fromEntries(this.mediaObjects.entries()),
      rubrics: this.rubrics.models,
      questions: this.questions.models,
      roles: this.roles.models,
    }

    const { errors, warnings } = await validateSlideDeck(
      context,
      this.repository
    )

    // ashold12 - commenting this out as it creates potentially problematic/unintended rubric
    // modifications that are un-recoverable post-publish. Seems bad but I'm going to keep it in comment
    // for no in case we want to bring it back for some reason
    //
    // for (const result of errors) {
    //   if (result.key === 'rubric_must_have_existing_slide') {
    //     const rubricId = result.options?.rubricId
    //     if (rubricId) {
    //       // fix rubric with non-existing slide
    //       // this.fixRubricAttachedToMissingSlide(rubricId)
    //     }
    //   }
    // }

    return { errors, warnings }
  }

  @action
  fixRubricAttachedToMissingSlide = async (rubricId: string) => {
    const rubric = this.rubrics.models.find((r) => r.id === rubricId)
    if (!rubric) return

    // fix the rubric by setting the slideId to ''
    this.saveSlideRubric({
      rubricId: rubricId,
      rubricFields: {
        ...rubric.data,
        slideId: '',
      },
    })
  }

  createReviewInviteAndMailtoLink = async (originURL: string) => {
    const invitationRef = await createSlideDeckReviewInvitation(
      this.repository,
      {
        slideDeckId: this.slideDeckId,
      }
    )

    const subject = `Review Requested: DRAFT ${this.slideDeck.data.slideDeckName} (${this.slideDeck.data.slideDeckVersion})`

    const body = getReviewEmailBody({
      originURL,
      invitationId: invitationRef.id,
      slideDeckName: this.slideDeck.data.slideDeckName,
      slides: this.slides.models,
      fullName: this.repository.breakoutUser?.user.fullName || '',
    })

    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  @computed
  get featuredSlideDecksOfType() {
    return this.slideDecksOfType.models.filter(
      (slideDeck) =>
        slideDeck.data.slideDeckFeatured && slideDeck.id !== this.slideDeckId
    )
  }

  touchSlide(slideId: string) {
    return touchSlide(this.repository, {
      slideDeckId: this.slideDeckId,
      slideId,
    })
  }

  touchSlideDeck() {
    return touchSlideDeck(this.repository, { slideDeckId: this.slideDeckId })
  }

  withTouchSlideDeck = async <T>(fn: () => Promise<T>): Promise<T> => {
    const result = await fn()
    await this.touchSlideDeck()
    return result
  }

  /****************************************************************
   *  Materials
   ***************************************************************/

  reorderSlideDeckMaterials = async (
    currentOrder: string[],
    { oldIndex, newIndex }: { oldIndex: number; newIndex: number }
  ): Promise<void> => {
    await sortSlideDeckMaterials(this.repository, {
      currentOrder,
      oldIndex,
      newIndex,
      slideDeckId: this.slideDeckId,
    })
  }

  deleteSlideDeckMaterial = async (
    materialId: string,
    materialType: SlideDeckMaterialType
  ): Promise<void> => {
    await deleteSlideDeckMaterial(this.repository, {
      slideDeckId: this.slideDeckId,
      materialId,
      materialType,
    })
  }

  /** field name is the name of the material field which holds the URL for the uploaded resource
   * should be **materialLink** in all cases except podcast images
   */
  deleteSlideDeckMaterialFile = async ({
    fieldName,
    materialId,
    materialType,
  }: {
    fieldName: 'materialLink' | 'imageUrl'
    materialId: string
    materialType: SlideDeckMaterialType
  }) => {
    await deleteSlideDeckMaterialFile(this.repository, {
      slideDeckId: this.slideDeckId,
      fieldName,
      materialId,
      materialType,
    })
  }

  /** if materialId is not present, creates a new material document and returns the ID */
  saveSlideDeckMaterial = async ({
    materialFields,
    materialId,
  }: Omit<Parameters<typeof saveSlideDeckMaterial>[1], 'slideDeckId'>) => {
    return await saveSlideDeckMaterial(this.repository, {
      slideDeckId: this.slideDeckId,
      materialId,
      materialFields,
    })
  }

  /**
   * returns the url of the uploaded material
   */
  uploadSlideDeckMaterialFile = async ({
    materialId,
    materialType,
    file,
  }: Omit<
    Parameters<typeof uploadSlideDeckMaterialFile>[1],
    'slideDeckId'
  >) => {
    return await uploadSlideDeckMaterialFile(this.repository, {
      slideDeckId: this.slideDeckId,
      materialId,
      materialType,
      file,
    })
  }

  /****************************************************************
   *  Slide
   ***************************************************************/
  saveSlide = async (
    params: Omit<Parameters<typeof saveSlide>[1], 'slideDeckId'>
  ) => {
    return this.withTouchSlideDeck(() => {
      return saveSlide(this.repository, {
        ...params,
        slideDeckId: this.slideDeckId,
      })
    })
  }

  deleteSlide = async (
    params: Omit<Parameters<typeof deleteSlide>[1], 'slideDeckId'>
  ) => {
    // find all interactiveElements w/ slideId as groupSlideId or slideId
    const slidesToUpdates = this.questions.models.filter(
      (question) =>
        question.data.slideId === params.slideId ||
        question.data.groupSlideId === params.slideId
    )

    // if interactive element is only on this slide, delete it
    // else, update it to remove the slideId or groupSlideId
    const slideUpdatePromises = slidesToUpdates.map((question) => {
      const newData = { ...question.data }
      if (question.data.slideId === params.slideId) newData.slideId = ''
      if (question.data.groupSlideId === params.slideId)
        newData.groupSlideId = ''

      if (!newData.slideId && !newData.groupSlideId)
        return this.deleteSlideDeckQuestion(question.id)

      return this.saveSlideDeckQuestion({
        questionId: question.id,
        questionFields: newData,
      })
    })

    return await Promise.all([
      ...slideUpdatePromises,
      deleteSlide(this.repository, {
        ...params,
        slideDeckId: this.slideDeckId,
      }),
    ])
  }

  uploadSlideVideo = async (
    params: Omit<Parameters<typeof uploadSlideVideo>[1], 'slideDeckId'>
  ) => {
    return await uploadSlideVideo(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  uploadSlideImage = async (
    params: Omit<Parameters<typeof uploadSlideImage>[1], 'slideDeckId'>
  ) => {
    return await uploadSlideImage(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  deleteSlideVideo = async (
    params: Omit<Parameters<typeof deleteSlideVideo>[1], 'slideDeckId'> & {
      fileExtension: string
    }
  ) => {
    return await deleteSlideVideo(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  deleteSlideImage = async (
    params: Omit<Parameters<typeof deleteSlideImage>[1], 'slideDeckId'> & {
      fileExtension: string
    }
  ) => {
    return await deleteSlideImage(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  sortSlides = async (
    params: Omit<Parameters<typeof sortSlides>[1], 'slideDeckId'>
  ) => {
    return await sortSlides(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  /****************************************************************
   *  Author
   ***************************************************************/
  uploadSlideDeckAuthorImage = async ({
    authorId,
    file,
  }: {
    authorId: string
    file: File
  }) => {
    return await uploadSlideDeckAuthorImage(this.repository, {
      slideDeckId: this.slideDeckId,
      authorId,
      file,
    })
  }

  /**
   * Returns an authorId.
   */
  saveSlideDeckAuthor = async ({
    authorFields,
    authorId,
  }: {
    authorId?: string
    authorFields: AuthorFieldsForUpload
  }) => {
    return await saveSlideDeckAuthor(this.repository, {
      slideDeckId: this.slideDeckId,
      authorFields,
      authorId,
    })
  }

  deleteSlideDeckAuthor = async (authorId: string) => {
    await deleteSlideDeckAuthor(this.repository, {
      slideDeckId: this.slideDeckId,
      authorId,
    })
  }

  deleteSlideDeckAuthorImage = async (authorId: string) => {
    await deleteSlideDeckAuthorImage(this.repository, {
      slideDeckId: this.slideDeckId,
      authorId,
    })
  }

  reorderSlideDeckAuthors = async (
    currentOrder: string[],
    { oldIndex, newIndex }: { oldIndex: number; newIndex: number }
  ): Promise<void> => {
    await sortSlideDeckAuthors(this.repository, {
      currentOrder,
      oldIndex,
      newIndex,
      slideDeckId: this.slideDeckId,
    })
  }

  uploadSlideDeckExhibitImage = async ({
    exhibitId,
    file,
  }: {
    exhibitId: string
    file: File
  }) => {
    return await uploadSlideDeckExhibitImage(this.repository, {
      slideDeckId: this.slideDeckId,
      exhibitId,
      file,
    })
  }

  /****************************************************************
   *  Exhibits
   ***************************************************************/

  saveSlideDeckExhibit = async ({
    exhibitFields,
    exhibitId,
  }: {
    exhibitId?: string
    exhibitFields: ExhibitFieldsForUpload
  }) => {
    return await saveSlideDeckExhibit(this.repository, {
      slideDeckId: this.slideDeckId,
      exhibitFields,
      exhibitId,
    })
  }

  deleteSlideDeckExhibit = async (exhibitId: string) => {
    await deleteSlideDeckExhibit(this.repository, {
      slideDeckId: this.slideDeckId,
      exhibitId,
    })
  }

  deleteSlideDeckExhibitImage = async (exhibitId: string) => {
    await deleteSlideDeckExhibitImage(this.repository, {
      slideDeckId: this.slideDeckId,
      exhibitId,
    })
  }

  /****************************************************************
   *  Roles
   ***************************************************************/

  /**
   * Returns a roleId when creating a new role.
   */
  saveSlideDeckRole = async ({
    roleFields,
    roleId,
  }: {
    roleId?: string
    roleFields: RoleFields
  }) => {
    return await saveSlideDeckRole(this.repository, {
      slideDeckId: this.slideDeckId,
      roleFields,
      roleId,
    })
  }

  deleteSlideDeckRole = async (roleId: string) => {
    await deleteSlideDeckRole(this.repository, {
      slideDeckId: this.slideDeckId,
      roleId,
    })
  }

  reorderSlideDeckRoles = async (
    currentOrder: string[],
    { oldIndex, newIndex }: { oldIndex: number; newIndex: number }
  ): Promise<void> => {
    await sortSlideDeckRoles(this.repository, {
      currentOrder,
      oldIndex,
      newIndex,
      slideDeckId: this.slideDeckId,
    })
  }

  /****************************************************************
   *  Questions/Interactive Elements
   ***************************************************************/

  sortSlideDeckQuestions = async (
    params: Omit<Parameters<typeof sortSlideQuestions>[1], 'slideDeckId'>
  ) => {
    return await sortSlideQuestions(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  saveSlideDeckQuestion = async ({
    questionFields,
    questionId,
  }: {
    questionId?: string
    questionFields: QuestionFieldsForUpload
  }) => {
    return await saveSlideDeckQuestion(this.repository, {
      slideDeckId: this.slideDeckId,
      questionFields,
      questionId,
    })
  }

  deleteSlideDeckQuestion = async (questionId: string) => {
    await deleteSlideDeckQuestion(this.repository, {
      slideDeckId: this.slideDeckId,
      questionId,
    })
  }

  deleteSlideRubric = async (
    params: Omit<Parameters<typeof deleteSlideRubric>[1], 'slideDeckId'>
  ) => {
    return await deleteSlideRubric(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  getTalkTrackPreview = async (
    questionId: string,
    forcePumpQuestion?: boolean
  ) => {
    return getTalkTrackAudio(this.repository, {
      questionId,
      forcePumpQuestion,
      slideDeckId: this.slideDeckId,
    })
  }

  getSoloBotPreview = async (
    questionId: string,
    forcePumpQuestion?: boolean
  ) => {
    return getSoloBotAudio(this.repository, {
      questionId,
      forcePumpQuestion,
      slideDeckId: this.slideDeckId,
    })
  }

  /****************************************************************
   *  Rubric
   ***************************************************************/

  saveSlideRubric = async (
    params: Omit<Parameters<typeof saveSlideRubric>[1], 'slideDeckId'>
  ) => {
    return await saveSlideRubric(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  assignSlideRubricToSlide = async (
    params: Omit<Parameters<typeof assignSlideRubricToSlide>[1], 'slideDeckId'>
  ) => {
    return await assignSlideRubricToSlide(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  /****************************************************************
   *  User Assignment
   ***************************************************************/

  get primaryDesignerProfile() {
    return this.primaryDesignerSearch
  }

  get designedForInstructorProfile() {
    return this.designedForInstructorSearch
  }

  restartPrimaryDesignerStream(userId: string | undefined) {
    this.removeStream('primaryDesignerSearch')
    if (userId) {
      this.addStream(
        getUserSearch(this.repository, userId),
        (search) =>
          runInAction(() => {
            this.primaryDesignerSearch = search
          }),
        {
          name: 'primaryDesignerSearch',
          onError: () =>
            runInAction(() => {
              this.primaryDesignerSearch = null
            }),
        }
      )
    } else {
      runInAction(() => {
        this.primaryDesignerSearch = null
      })
    }
  }

  restartDesignedForInstructorStream(userId: string | undefined) {
    this.removeStream('designedForInstructorSearch')
    if (userId) {
      this.addStream(
        getUserSearch(this.repository, userId),
        (search) =>
          runInAction(() => {
            this.designedForInstructorSearch = search
          }),
        {
          name: 'designedForInstructorSearch',
          onError: () =>
            runInAction(() => {
              this.designedForInstructorSearch = null
            }),
        }
      )
    } else {
      runInAction(() => {
        this.designedForInstructorSearch = null
      })
    }
  }

  setPrimaryDesigner = async (userSearch: UserSearch | null) => {
    const userId = userSearch?.id ?? ''
    await this.saveSlideDeckForm({ primaryDesignerUserId: userId })
    this.restartPrimaryDesignerStream(userId || undefined)
  }

  setDesignedForInstructor = async (userSearch: UserSearch | null) => {
    const userId = userSearch?.id ?? ''
    await this.saveSlideDeckForm({ designedForInstructorUserId: userId })
    this.restartDesignedForInstructorStream(userId || undefined)
  }

  searchEmployees = async (filter: string) => {
    return fetchUserSearch(
      this.repository,
      ['editor', 'author', 'admin', 'corre'],
      filter
    )
  }

  searchInstructors = async (filter: string) => {
    return fetchUserSearch(this.repository, 'instructor', filter)
  }

  /****************************************************************
   *  Slide Details
   ***************************************************************/

  saveSlideDeckForm = async (
    params: Partial<Parameters<typeof saveSlideDeckForm>[2]>
  ) => {
    const payload = {
      slideDeckDescription: this.slideDeck.data.slideDeckDescription,
      slideDeckDisciplines: this.slideDeck.data.slideDeckDisciplines,
      slideDeckLearningObjectives:
        this.slideDeck.data.slideDeckLearningObjectives,
      slideDeckIndustries: this.slideDeck.data.slideDeckIndustries,
      slideDeckFeatured: this.slideDeck.data.slideDeckFeatured,
      slideDeckGoogleTemplateURL:
        this.slideDeck.data.slideDeckGoogleTemplateURL,
      slideDeckName: this.slideDeck.data.slideDeckName,
      slideDeckPrice: this.slideDeck.data.slideDeckPrice,
      slideDeckTeaser: this.slideDeck.data.slideDeckTeaser,
      slideDeckVersion: this.slideDeck.data.slideDeckVersion,
      slideDeckImageURL: this.slideDeck.data.slideDeckImageURL,
      ...params,
    }
    return await saveSlideDeckForm(this.repository, this.slideDeckId, payload)
  }

  deepCopySlideDeck = async (
    params: Omit<Parameters<typeof deepCopySlideDeck>[1], 'slideDeck'>
  ) => {
    return await deepCopySlideDeck(this.repository, {
      ...params,
      slideDeck: this.slideDeck,
    })
  }

  uploadSlideDeckImage = async (file: File) => {
    return await uploadSlideDeckImage(this.repository, {
      slideDeckId: this.slideDeckId,
      file,
      // if existing logo, we are replacing and need to include qs on upload to cache bust
      includeQueryString: !!this.slideDeck.data.slideDeckImageURL,
    })
  }

  deleteSlideDeckImage = async () => {
    return await deleteSlideDeckImage(this.repository, this.slideDeckId)
  }

  addCatalogToSlideDeck = async (
    params: Omit<Parameters<typeof addCatalogToSlideDeck>[1], 'slideDeckId'>
  ) => {
    return await addCatalogToSlideDeck(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  removeCatalogFromSlideDeck = async (
    params: Omit<
      Parameters<typeof removeCatalogFromSlideDeck>[1],
      'slideDeckId'
    > & {
      moduleData: {
        teachingPlan: TeachingPlan
        module: TeachingPlanModule
      }[]
    }
  ) => {
    return await runTransaction(
      this.repository.firestore,
      async (transaction) => {
        const catalogReq = removeCatalogFromSlideDeck(this.repository, {
          ...params,
          slideDeckId: this.slideDeckId,
          transaction,
        })
        const removeModuleReqs = params.moduleData.map((moduleData) => {
          return deleteTeachingPlanModuleSlideDeck(this.repository, {
            slideDeckId: this.slideDeckId,
            transaction,
            teachingPlanId: moduleData.teachingPlan.id,
            moduleId: moduleData.module.id,
            catalogId: params.catalogId,
          })
        })
        await Promise.all([catalogReq, ...removeModuleReqs])
      }
    )
  }

  replaceVersionsInCatalog = async ({
    catalogId,
    existingVersions,
    slideDeckIdsToUnFeature,
    moduleSlideDeckConfirmationResult,
  }: {
    catalogId: string
    existingVersions: string[]
    slideDeckIdsToUnFeature: string[]
    moduleSlideDeckConfirmationResult: {
      result: 'replace' | 'remove' | 'notShown'
      modulesToEdit: {
        catalogId: string
        moduleId: string
        teachingPlanId: string
        moduleSlideDeckId: string
        order: number | undefined
      }[]
    }
  }) => {
    const moduleEditError = new Error(
      'if modulesToEdit are present, module slide deck confirmation result must be replace or remove'
    )
    if (
      moduleSlideDeckConfirmationResult.modulesToEdit.length &&
      moduleSlideDeckConfirmationResult.result === 'notShown'
    )
      throw moduleEditError

    // get the intersection of the existing versions and the slideDeckIdsToUnFeature
    // so we can do it in the same request (transaction order is not consistent)
    const slideDeckIdsToUnFeatureAndRemoveFromCatalog =
      slideDeckIdsToUnFeature.filter((id) => {
        return existingVersions.includes(id)
      })

    // AFAIK there's no path for this list to be populated as of now but
    // keeping it here for future proofing
    const slideDeckIdsToUnFeatureAndMaintainCatalogs =
      slideDeckIdsToUnFeature.filter((id) => {
        return !slideDeckIdsToUnFeatureAndRemoveFromCatalog.includes(id)
      })

    const slideDeckIdsToRemoveFromCatalog = existingVersions.filter((id) => {
      return !slideDeckIdsToUnFeatureAndRemoveFromCatalog.includes(id)
    })

    await runTransaction(this.repository.firestore, async (transaction) => {
      const unFeatureRequests = slideDeckIdsToUnFeatureAndMaintainCatalogs.map(
        (slideDeckId) =>
          updateSlideDeckFeatured(this.repository, {
            slideDeckId,
            slideDeckFeatured: false,
            transaction,
          })
      )
      const unFeatureAndRemoveCatalogRequests =
        slideDeckIdsToUnFeatureAndRemoveFromCatalog.map((slideDeckId) =>
          removeCatalogFromSlideDeckAndUnFeature(this.repository, {
            slideDeckId,
            catalogId,
            transaction,
          })
        )
      const removeCatalogRequests = slideDeckIdsToRemoveFromCatalog.map(
        (slideDeckId) =>
          removeCatalogFromSlideDeck(this.repository, {
            slideDeckId,
            catalogId,
            transaction,
          })
      )

      // current slide deck id comes from the cubit/page
      const addCatalogToCurrentSlideDeck = addCatalogToSlideDeck(
        this.repository,
        { slideDeckId: this.slideDeckId, catalogId, transaction }
      )

      // replace or remove module slide decks
      const removeModules = moduleSlideDeckConfirmationResult.modulesToEdit.map(
        (moduleData) => {
          if (moduleSlideDeckConfirmationResult.result === 'notShown')
            throw moduleEditError
          return deleteTeachingPlanModuleSlideDeck(this.repository, {
            slideDeckId: moduleData.moduleSlideDeckId,
            transaction: transaction,
            teachingPlanId: moduleData.teachingPlanId,
            moduleId: moduleData.moduleId,
            catalogId: moduleData.catalogId,
          })
        }
      )

      // don't add the new modules unless user confirmed replace
      const addModules =
        moduleSlideDeckConfirmationResult.result === 'replace'
          ? moduleSlideDeckConfirmationResult.modulesToEdit.map(
              (moduleData) => {
                return saveTeachingPlanModuleSlideDeck(this.repository, {
                  transaction: transaction,
                  teachingPlanId: moduleData.teachingPlanId,
                  moduleId: moduleData.moduleId,
                  catalogId: moduleData.catalogId,
                  slideDeckOrder:
                    moduleData.order === undefined ? 999 : moduleData.order,
                  slideDeckId: this.slideDeckId,
                  slideDeckTypeId: this.slideDeck.data.slideDeckTypeId,
                })
              }
            )
          : []

      await Promise.all([
        ...unFeatureRequests,
        ...unFeatureAndRemoveCatalogRequests,
        ...removeCatalogRequests,
        addCatalogToCurrentSlideDeck,
        ...removeModules,
        ...addModules,
      ])
    })
  }

  /**
   * @param slideDeckId - if not provided, will fork the current slide deck
   */
  forkSlideDeck = async (slideDeckId?: string) => {
    const slideDeckIdToFork = slideDeckId || this.slideDeckId
    await forkSlideDeck(this.repository, { slideDeckId: slideDeckIdToFork })
  }

  setSlideDeckHidden = async () => {
    await hideSlideDeck(this.repository, {
      slideDeckId: this.slideDeckId,
    })
  }

  setSlideDeckVisible = async () => {
    await showSlideDeck(this.repository, {
      slideDeckId: this.slideDeckId,
    })
  }

  updateSlideDeckFeatured = async (
    params: Parameters<typeof updateSlideDeckFeatured>[1]
  ) => {
    return await updateSlideDeckFeatured(this.repository, {
      ...params,
    })
  }

  setDemoSlideDeckPublished = async (replaceExisting: boolean) => {
    if (this.slideDeck.data.slideDeckType !== SlideDeckType.demo)
      throw new Error(
        'setDemoSlideDeckPublished can only be called for demo slide decks'
      )

    // if replace was specified, un-feature existing demos of this type
    // this will move the other demos to archived state prior to publishing the new one
    if (replaceExisting && this.publishedDemosOfType.length > 0)
      await Promise.all(
        this.publishedDemosOfType.map((s) =>
          updateSlideDeckFeatured(this.repository, {
            slideDeckId: s.id,
            slideDeckFeatured: false,
          })
        )
      )

    // if draft mark published, slide deck will be immutable
    if (this.slideDeck.slideDeckStatus === SlideDeckStatus.draft)
      await updateSlideDeckState(this.repository, {
        slideDeckId: this.slideDeckId,
        slideDeckState: SlideDeckState.published,
      })

    // updated feature to true -> demo slide deck will appear as published
    await updateSlideDeckFeatured(this.repository, {
      slideDeckId: this.slideDeckId,
      slideDeckFeatured: true,
    })
  }

  deleteSlideDeck = async (params: Parameters<typeof deleteSlideDeck>[1]) => {
    return await deleteSlideDeck(this.repository, {
      ...params,
    })
  }

  async addFavoriteSlideDeck(slideDeck: SlideDeck): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    const slideDeckId = slideDeck.id
    runInAction(() => {
      this.favoritingSlideDeckIds.add(slideDeckId)
    })
    try {
      await addUserProfileFavoriteSlideDeck(this.repository, {
        userId,
        slideDeckId: slideDeckId,
        slideDeckTypeId: slideDeck.data.slideDeckTypeId,
      })
    } finally {
      runInAction(() => {
        this.favoritingSlideDeckIds.delete(slideDeckId)
      })
    }
  }

  async removeFavoriteSlideDeck(slideDeck: SlideDeck): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    const slideDeckId = slideDeck.id
    runInAction(() => {
      this.favoritingSlideDeckIds.add(slideDeckId)
    })
    try {
      await removeUserProfileFavoriteSlideDeck(this.repository, {
        userId,
        slideDeckId: slideDeckId,
      })
    } finally {
      runInAction(() => {
        this.favoritingSlideDeckIds.delete(slideDeckId)
      })
    }
  }

  /****************************************************************
   *  References
   ***************************************************************/
  saveReference = async (
    params: Omit<Parameters<typeof saveSlideDeckReference>[1], 'slideDeckId'>
  ) => {
    return await saveSlideDeckReference(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  deleteReference = async (
    params: Omit<Parameters<typeof deleteSlideDeckReference>[1], 'slideDeckId'>
  ) => {
    return await deleteSlideDeckReference(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  sortReferences = async (
    params: Omit<Parameters<typeof sortSlideDeckReferences>[1], 'slideDeckId'>
  ) => {
    return await sortSlideDeckReferences(this.repository, {
      ...params,
      slideDeckId: this.slideDeckId,
    })
  }

  @action
  async ensureDevModeRoom() {
    const roomState = await ensureDevRoomState(this.repository, {
      slideDeckId: this.slideDeckId,
      userId: this.repository.uid,
    })

    return roomState
  }

  /****************************************************************
   *  teaching plans / modules
   ***************************************************************/
  private getTeachingPlanKey = ({
    catalogId,
    teachingPlanId,
  }: {
    catalogId: string
    teachingPlanId: string
  }) => `teachingPlan-${catalogId}-${teachingPlanId}`

  private getTeachingPlanModuleKey = ({
    catalogId,
    teachingPlanId,
    moduleId,
  }: {
    catalogId: string
    teachingPlanId: string
    moduleId: string
  }) => `module-${catalogId}-${teachingPlanId}-${moduleId}`

  private initTeachingPlanStreams = async (
    streamData: { catalogId: string; teachingPlanId: string }[]
  ) => {
    const streamDataWithKeys = streamData.map((data) => ({
      ...data,
      key: this.getTeachingPlanKey(data),
    }))

    streamDataWithKeys.forEach(({ catalogId, teachingPlanId, key }) => {
      if (this.hasStream(key)) return
      this._teachingPlanDataByIds[key] = TeachingPlan.empty(this.repository)
      this.addStream(
        getTeachingPlan(this.repository, {
          catalogId,
          teachingPlanId,
        }),
        (teachingPlan) => {
          this._teachingPlanDataByIds[key].replaceModel(teachingPlan)
        },
        { name: key, namespace: 'teaching-plan' }
      )
    })
  }

  private initTeachingPlanModuleStreams = async (
    streamData: {
      catalogId: string
      teachingPlanId: string
      moduleId: string
    }[]
  ) => {
    const streamDataWithKeys = streamData.map((data) => ({
      ...data,
      key: this.getTeachingPlanModuleKey(data),
    }))

    streamDataWithKeys.forEach(
      ({ catalogId, teachingPlanId, key, moduleId }) => {
        if (this.hasStream(key)) return
        this._teachingPlanModulesByIds[key] = TeachingPlanModule.empty(
          this.repository
        )
        this.addStream(
          getTeachingPlanModule(this.repository, {
            catalogId,
            teachingPlanId,
            moduleId,
          }),
          (module) => {
            this._teachingPlanModulesByIds[key].replaceModel(module)
          },
          { name: key, namespace: 'teaching-plan-module' }
        )
      }
    )
  }
}

function slideQuestionBucketTypeFromQuestionType(
  questionType: SlideQuestionType
): SlideQuestionBucketType {
  switch (questionType) {
    // title is on any slide, managed by client
    case SlideQuestionType.title:
      return SlideQuestionBucketType.title

    // on same slide
    case SlideQuestionType.sorting:
    case SlideQuestionType.multipleChoice:
      return SlideQuestionBucketType.quiz

    // on same slide
    case SlideQuestionType.poll:
    case SlideQuestionType.customPoll:
      return SlideQuestionBucketType.poll

    // order is not observed by client here
    case SlideQuestionType.talkTrack:
      return SlideQuestionBucketType.talkTrack

    // there should never be more than one of these per slide, so sort is irrelevant
    case SlideQuestionType.moderatorBot:
    case SlideQuestionType.graderBot:
    case SlideQuestionType.tutorBot:
    case SlideQuestionType.rolePlayBot:
    case SlideQuestionType.formCompletionBot:
      return SlideQuestionBucketType.soloBot

    case SlideQuestionType.aiAgent:
      return SlideQuestionBucketType.aiAgent

    case SlideQuestionType.moderationTask:
      return SlideQuestionBucketType.moderationTask

    case SlideQuestionType.displayModerationTask:
      return SlideQuestionBucketType.displayModerationTask

    case SlideQuestionType.sortableStringList:
      return SlideQuestionBucketType.sortableStringList

    case SlideQuestionType.popUpInstructions:
      return SlideQuestionBucketType.popUpInstructions

    case SlideQuestionType.interactiveFormElement:
      return SlideQuestionBucketType.interactiveFormElement

    case SlideQuestionType.suggestedTimer:
      return SlideQuestionBucketType.suggestedTimer
  }
}

function getReviewEmailBody({
  slides,
  fullName,
  slideDeckName,
  originURL,
  invitationId,
}: {
  slides: SlideModel[]
  fullName: string
  slideDeckName: string
  originURL: string
  invitationId: string
}) {
  return `
Hello Professor,

Your Breakout Learning assignment draft is ready for review.

Assignment: ${slideDeckName}
Review Link: ${originURL}/invitation/${invitationId}

Please reply directly to this email with any feedback on the Discussion Experience, Quiz, and Pre-Work materials

We'd like to know whether you believe this draft is ready to publish or how we can adjust it to better support your goals.

Feel free to share as much or as little detail as you’d like.

Notes:
[Add your comments here]

Discussion Experience:
[Add individual slide comments as appropriate below]

${slides.map((slide, index) => `${index + 1}. ${slide.data.slideName}`).join('\n')}

Thank you,

${fullName}
Breakout Learning
      `
}
